import React from "react";
import { Link, useLocation } from "react-router";
import { SvgIcon, SvgIconType } from "@/svg";
import { LogoutButton } from "@/components/LogoutButton";

export type SettingsSidebarItem = {
  label: string;
  icon: SvgIconType;
  soon?: boolean;
} & ({ path: string } | { action: () => void });

export type SettingsSidebarGroup = {
  items: SettingsSidebarItem[];
};

export type SettingsSidebarProps = {
  groups: SettingsSidebarGroup[];
};

function isActionItem(
  item: SettingsSidebarItem,
): item is { label: string; icon: SvgIconType; soon?: boolean; action: () => void } {
  return "action" in item;
}

function isPathItem(
  item: SettingsSidebarItem,
): item is { label: string; icon: SvgIconType; soon?: boolean; path: string } {
  return "path" in item;
}

export function SettingsSidebar({ groups }: SettingsSidebarProps) {
  const location = useLocation();

  const Item = ({ item }: { item: SettingsSidebarItem }) => {
    const isActive = isPathItem(item) && location.pathname.endsWith(item.path);
    const isDisabled = item.soon;

    const handleItemClick = (e: React.MouseEvent) => {
      if (isActionItem(item)) {
        e.preventDefault();
        item.action();
      }
    };

    const Container = (props: React.PropsWithChildren) => {
      if (isDisabled) {
        return props.children;
      }
      if (isActionItem(item)) {
        return <button onClick={handleItemClick}>{props.children}</button>;
      }
      return <Link to={item.path}>{props.children}</Link>;
    };

    return (
      <Container>
        <div
          className={`flex items-center gap-4 text-sm pt-2 px-6 p-2 ${
            isActive
              ? "bg-pureWhite text-dark font-medium rounded-lg"
              : isDisabled
                ? "text-backgroundGray cursor-not-allowed"
                : "text-feintGray hover:bg-pureWhite hover:text-dark rounded-lg cursor-pointer"
          }`}
        >
          <SvgIcon icon={item.icon} />
          {item.label} {item.soon && "(soon)"}
        </div>
      </Container>
    );
  };

  const Group = ({ group }: { group: SettingsSidebarGroup }) => {
    return (
      <div className="flex flex-col gap-2">
        {group.items.map((item, idx) => {
          const key = isPathItem(item) ? item.path : `item-${idx}`;
          return <Item key={key} item={item} />;
        })}
      </div>
    );
  };

  return (
    <div className="w-64 h-full p-6 gap-5 flex flex-col">
      <h1 className="text-xl font-medium text-feintGray">Settings</h1>
      <div className="flex flex-col gap-2">
        <Link to="/">
          <div className="flex items-center gap-4 overflow-auto text-light border-b border-light pb-4 pl-4 pr-3 mb-4 cursor-pointer hover:text-dark">
            <SvgIcon icon="DoubleChevronLeft" />
            Back to Home
          </div>
        </Link>
        {groups.map((group, index) => (
          <Group key={index} group={group} />
        ))}

        <div className="px-2">
          <LogoutButton />
        </div>
      </div>
    </div>
  );
}
